import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import {
  ClickAwayListener,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  useTheme,
} from '@mui/material';

import { ReactComponent as AttachmentsIcon } from '@/assets/icons/AttachFileRounded.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trash-icon.svg';
import {
  ButtonsList,
  Container,
  PostCommentButton,
  PriorityButton,
  PriorityButtonsGroup,
  PriorityContainer,
  PriorityLabel,
} from '@/features/comments/ui/CommentOrTaskForm/CommentOrTaskForm.styles';
import AppButton from '@/shared/ui/AppButton';

import Quill from './QuillEditor';
import './quill.css';

interface Props {
  type: 'comment' | 'task';
  buttonLabel?: string;
  onTaskSave?: (content: string, priority?: 'LOW' | 'MEDIUM' | 'HIGH') => void;
  onCommentSave?: (params: {
    message: string;
    files?: File[];
    commentId?: string;
    parentId?: string;
    edit?: boolean;
  }) => void;
  onCancel?: () => void;
  isReply?: boolean;
  isEdit?: boolean;
  commentId?: string;
  parentId?: string;
  comment?: string;
  onClose?: () => void;
  greenVariant?: boolean;
  draft?: string;
  onDraftChange?: (plainText: string, deltaJson: string) => void;
}

const CommentOrTaskForm: FC<Props> = ({
  type,
  buttonLabel,
  onTaskSave,
  onCommentSave,
  onCancel,
  isReply,
  isEdit,
  commentId,
  parentId,
  comment,
  onClose,
  greenVariant,
  draft,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isGreenVariant = greenVariant || type === 'task';

  const [content, setContent] = useState<string>(
    (type === 'task' || isEdit) && comment ? comment : (draft ?? '{ "ops": [] }'),
  );
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');
  const [files, setFiles] = useState<File[]>([]);

  const {
    getRootProps,
    getInputProps,
    open: openPicker,
  } = useDropzone({
    noClick: true,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'image/webp': [],
      'application/pdf': [],
      'text/plain': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
    },
    noDrag: true,
    onDrop: (files: File[]) => setFiles((prev) => [...prev, ...files]),
  });
  const quillRef = useRef<Quill>(null);
  const toolbarContainerRef = useRef<HTMLDivElement>(null);
  const isMouseDownInsideRef = useRef(false);

  const editorId = useMemo(() => {
    return Array.from({ length: 20 }, () =>
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(
        Math.floor(Math.random() * 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.length),
      ),
    ).join('');
  }, []);

  useEffect(() => {
    if (!quillRef.current) {
      quillRef.current = new Quill(`#${editorId}`, {
        theme: 'snow',
        placeholder: t('Write something here...'),
        formats: ['bold', 'italic', 'underline', 'strike'],
        modules: {
          toolbar: isReply || type === 'task' ? [] : [['bold', 'italic', 'underline', 'strike']],
        },
      });

      if ((type === 'task' || isEdit) && comment) {
        quillRef.current.setContents(JSON.parse(comment));
        setTimeout(() => {
          quillRef.current?.setSelection(quillRef.current?.getLength() ?? 0);
        }, 100);
      } else if (draft) {
        quillRef.current.setContents(JSON.parse(draft));
        setTimeout(() => {
          quillRef.current?.setSelection(quillRef.current?.getLength() ?? 0);
        }, 100);
      }

      quillRef.current.focus();

      quillRef.current.on('text-change', () => {
        const delta = quillRef.current?.getContents() || [];
        const deltaJson = JSON.stringify(delta);
        setContent(deltaJson);
        onDraftChange?.(quillRef.current?.getText().trim() ?? '', deltaJson);
      });

      if (toolbarContainerRef.current && !(isReply || type === 'task')) {
        const toolbarElement = document.querySelector(`.ql-toolbar`);

        if (toolbarElement) {
          toolbarContainerRef.current.appendChild(toolbarElement);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, editorId, isEdit, isReply, t, type]);

  const submitForm = useCallback(() => {
    if (type === 'task') {
      onTaskSave?.(content, priority);
    } else {
      onCommentSave?.({ message: content, files, parentId, edit: isEdit, commentId });
    }

    onClose?.();
    onCancel?.();
    setPriority('MEDIUM');
    setFiles([]);
    quillRef.current?.setContents([]);
  }, [
    commentId,
    content,
    files,
    isEdit,
    onCancel,
    onCommentSave,
    onTaskSave,
    parentId,
    priority,
    type,
    onClose,
  ]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isMouseDownInsideRef.current) {
          isMouseDownInsideRef.current = false;
          return;
        }
        onClose?.();
      }}
    >
      <Container
        onMouseDown={() => {
          isMouseDownInsideRef.current = true;
        }}
        onMouseUp={() => {
          isMouseDownInsideRef.current = false;
        }}
        type={type}
        isReply={isReply || type === 'task'}
        greenVariant={isGreenVariant}
        style={
          {
            '--accent-light': theme.palette.accent.light,
            '--accent-main': theme.palette.accent.main,
          } as React.CSSProperties
        }
      >
        <div id={editorId} style={{ minHeight: '6rem' }} />

        {type === 'task' && (
          <PriorityContainer>
            <PriorityLabel>{t('Priority')}:</PriorityLabel>
            <PriorityButtonsGroup>
              <PriorityButton active={priority === 'LOW'} onClick={() => setPriority('LOW')}>
                {t('LOW')}
              </PriorityButton>
              <PriorityButton active={priority === 'MEDIUM'} onClick={() => setPriority('MEDIUM')}>
                {t('MEDIUM')}
              </PriorityButton>
              <PriorityButton active={priority === 'HIGH'} onClick={() => setPriority('HIGH')}>
                {t('HIGH')}
              </PriorityButton>
            </PriorityButtonsGroup>
          </PriorityContainer>
        )}

        {!!files.length && (
          <List
            sx={{
              maxHeight: '150px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: (theme) => theme.palette.grey[300],
              },
            }}
          >
            {files.map((file: File, key: number) => (
              <ListItem
                key={key}
                sx={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[100]}`,
                }}
              >
                <ListItemText
                  sx={{
                    width: '100%',
                    '& > span': {
                      fontSize: '0.75rem !important',
                      lineHeight: '120%',
                    },
                  }}
                >
                  {file.name}
                </ListItemText>

                <ListItemButton
                  onClick={() => setFiles((prev) => prev.filter((p) => p.name !== file.name))}
                >
                  <TrashIcon />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        <ButtonsList type={type}>
          <Stack direction="row" alignItems="center">
            <div ref={toolbarContainerRef} style={{ display: 'flex', alignItems: 'center' }} />

            {!(isReply || type === 'task') && (
              <Divider
                flexItem
                orientation="vertical"
                sx={{
                  mx: 1,
                  my: '0.5rem',
                  borderWidth: 0.5,
                  borderColor: (theme) => theme.palette.grey[300],
                }}
              />
            )}

            <AppButton
              {...getRootProps()}
              disableRipple
              disableFocusRipple
              disableTouchRipple
              onClick={openPicker}
              variant="ghost"
              size="small"
              sx={{ px: '4px' }}
            >
              <input {...getInputProps()} />
              <AttachmentsIcon />
            </AppButton>
          </Stack>

          <Stack direction={'row'} alignItems={'center'}>
            <PostCommentButton
              data-testid="post-comment-button"
              greenVariant={isGreenVariant}
              onClick={submitForm}
            >
              {buttonLabel ?? t('Add Comment')}
            </PostCommentButton>
            {(isReply || type === 'task') && (
              <AppButton variant="ghost" size="small" onClick={onCancel}>
                {t('Cancel')}
              </AppButton>
            )}
          </Stack>
        </ButtonsList>
      </Container>
    </ClickAwayListener>
  );
};

export default CommentOrTaskForm;
